import { useState } from "react";
import cx from "classnames";

import Image from "common/components/Image";

import Button from "client/common/components/buttons/Button";
import Container from "client/common/components/layouts/Container";
import Modal from "client/common/components/deprecated-modals/Modal";
import ModalLayout from "client/common/components/deprecated-modals/ModalLayout";
import Accordion from "client/common/components/interactive/Accordion";
import HorizontalOverflow from "client/common/components/display/HorizontalOverflow";
import ProductLineCard from "client/products/components/ProductLineCard";
import { useProductLinesViewOnVisible, useViewItemListOnVisible } from "client/analytics/utils";
import { ProductLineListSource } from "client/analytics/types";

import { MyZoetisRewardsBlock as MyZoetisRewardsBlockType } from "../../types";
import { ZOETIS_CONTENT } from "../../constants";
import MarkdownText from "../helper/MarkdownText";
import arrow from "../../images/arrow.svg";
import Block from "../Block";

import styles from "../../styles/MyZoetisRewardsBlock.module.less";

type MyZoetisRewardsBlockProps = {
  className: string;
  block: MyZoetisRewardsBlockType;
};

const MyZoetisRewardsBlock = ({ block, className }: MyZoetisRewardsBlockProps) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const itemListRef = useViewItemListOnVisible(
    block.product_lines,
    ProductLineListSource.MyZoetisRewards,
  );
  const productLinesViewRef = useProductLinesViewOnVisible(
    block.product_lines,
    ProductLineListSource.MyZoetisRewards,
  );

  // Kept on FE since this will be the same across all MyZoetisRewards blocks
  const content = ZOETIS_CONTENT;

  return (
    <div
      className={cx(
        className,
        styles.container,
        styles[`visual-${block.config?.visual ?? "left"}`],
      )}
    >
      <Container>
        <div className={styles.info}>
          <div className={styles.content}>
            <h3 className={styles.header}>
              <MarkdownText value={content.header} />
            </h3>
            <div className={styles.facts}>
              {content.bullets.map((item, index) => (
                <div className={styles.fact} key={item.label}>
                  <div className={styles.number}>{`${index + 1}`.padStart(2, "0")}</div>
                  <div className={styles.texts}>
                    <p className={styles.label}>
                      <MarkdownText value={item.label} />
                    </p>
                    <div className={styles.description}>
                      <MarkdownText value={item.description} />
                    </div>
                  </div>
                </div>
              ))}
            </div>
            <div className={styles.action}>
              <Button href={content.action.url}>{content.action.label}</Button>
              <p className={styles.fine}>
                or read the program{" "}
                <button
                  className={styles.button}
                  type="button"
                  onClick={() => setIsModalOpen(true)}
                >
                  FAQs
                </button>
              </p>
              <Modal visible={isModalOpen} onClose={() => setIsModalOpen(false)}>
                <ModalLayout>
                  <ModalLayout.Header title="FAQs" onClose={() => setIsModalOpen(false)} />
                  <ModalLayout.Content>
                    <Accordion.Single
                      items={content.faqs.map((faq) => ({
                        key: faq.question,
                        label: <MarkdownText value={faq.question} />,
                        content: (
                          <div className={styles.modal}>
                            <MarkdownText.Block value={faq.answer} />
                          </div>
                        ),
                      }))}
                    />
                  </ModalLayout.Content>
                </ModalLayout>
              </Modal>
            </div>
          </div>
          <div className={styles.visual}>
            <Block block={content.visual} />
          </div>
        </div>
      </Container>
      <Container>
        <div className={styles.browse}>
          <Image className={styles.arrow} src={arrow} alt="arrow" />
          <div className={styles.headers}>
            <h3 className={styles.header}>
              <MarkdownText value={content.products.header} />
            </h3>
            <p className={styles.subheader}>
              <MarkdownText value={content.products.subheader} />
            </p>
          </div>
          <HorizontalOverflow
            ref={itemListRef}
            direction="right"
            classNames={{ scroller: styles.products, button: styles.button }}
          >
            {block.product_lines.map((productLine, index) => (
              <div
                key={productLine.id}
                ref={index === 0 ? productLinesViewRef : undefined}
                className={styles.product}
              >
                <ProductLineCard
                  productLine={productLine}
                  eventViewName={ProductLineListSource.MyZoetisRewards}
                  eventCategoryId={null}
                />
              </div>
            ))}
          </HorizontalOverflow>
        </div>
      </Container>
    </div>
  );
};

export default MyZoetisRewardsBlock;
